import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { useAuth } from '@/hooks/useAuth'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Upload, X, Image as ImageIcon, FileText } from 'lucide-react'
import { BUSINESS_SECTORS } from '@/utils/businessSectors'

const ListBusiness = () => {
  const [formData, setFormData] = useState({
    title: '',
    teaser: '',
    description: '',
    sector: '',
    price: '',
    revenue: '',
    ebitda: '',
    locationSuburb: '',
    locationState: '',
    images: [] as string[],
    requiresNDA: false,
  })
  
  const [isLoading, setIsLoading] = useState(false)
  const [uploadingImages, setUploadingImages] = useState(false)
  const [userRole, setUserRole] = useState<string | null>(null)
  const [checkingRole, setCheckingRole] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { user, loading } = useAuth()
  const { toast } = useToast()
  const navigate = useNavigate()

  useEffect(() => {
    if (!loading && !user) {
      navigate('/sign-in')
    }
  }, [user, loading, navigate])

  // Check if user has permission to create listings (broker or seller only)
  useEffect(() => {
    const checkUserRole = async () => {
      if (!user) {
        setCheckingRole(false)
        return
      }

      try {
        const { data: roles, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)

        if (error) throw error

        const userRoles = roles?.map(r => r.role) || []
        
        // Check if user is a broker or seller
        if (userRoles.includes('broker')) {
          setUserRole('broker')
        } else if (userRoles.includes('seller')) {
          setUserRole('seller')
        } else if (userRoles.includes('buyer')) {
          setUserRole('buyer')
          toast({
            title: 'Access Denied',
            description: 'Buyers cannot create business listings. Please register as a seller or broker to list businesses.',
            variant: 'destructive',
          })
          navigate('/dashboard')
        } else {
          // No recognized role
          toast({
            title: 'Access Denied',
            description: 'You do not have permission to create listings. Please contact support.',
            variant: 'destructive',
          })
          navigate('/dashboard')
        }
      } catch (error) {
        console.error('Error checking user role:', error)
        toast({
          title: 'Error',
          description: 'Failed to verify permissions',
          variant: 'destructive',
        })
        navigate('/dashboard')
      } finally {
        setCheckingRole(false)
      }
    }

    checkUserRole()
  }, [user, navigate, toast])

  // Show loading while auth or role is being checked
  if (loading || checkingRole) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-16">
          <div className="text-center">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    )
  }

  // Redirect if not authenticated or not authorized
  if (!user || !userRole || userRole === 'buyer') {
    return null
  }

  const handleInputChange = (field: string, value: string) => {
    if (field === 'requiresNDA') {
      setFormData(prev => ({ ...prev, [field]: value === 'true' }))
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleImageUpload = async (files: FileList) => {
    if (!files.length) return

    setUploadingImages(true)
    const newImages: string[] = []

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            title: 'Invalid File',
            description: `${file.name} is not an image file`,
            variant: 'destructive',
          })
          continue
        }

        // Validate file size (5MB limit)
        if (file.size > 5 * 1024 * 1024) {
          toast({
            title: 'File Too Large',
            description: `${file.name} is larger than 5MB`,
            variant: 'destructive',
          })
          continue
        }

        // Convert to base64 for preview (in a real app, you'd upload to cloud storage)
        const reader = new FileReader()
        const imageUrl = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(file)
        })

        newImages.push(imageUrl)
      }

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages].slice(0, 8) // Limit to 8 images
      }))

      if (newImages.length > 0) {
        toast({
          title: 'Images Added',
          description: `${newImages.length} image(s) added successfully`,
        })
      }
    } catch (error) {
      toast({
        title: 'Upload Error',
        description: 'Failed to process images',
        variant: 'destructive',
      })
    } finally {
      setUploadingImages(false)
    }
  }

  const removeImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }))
  }

  const handleSubmit = async (e: React.FormEvent, status: 'draft' | 'live' = 'draft') => {
    e.preventDefault()
    if (!user) return

    setIsLoading(true)

    try {
      const listingData = {
        title: formData.title,
        teaser: formData.teaser || null,
        description: formData.description || null,
        sector: formData.sector,
        price: formData.price ? parseFloat(formData.price) : null,
        revenue: formData.revenue ? parseFloat(formData.revenue) : null,
        ebitda: formData.ebitda ? parseFloat(formData.ebitda) : null,
        location_suburb: formData.locationSuburb || null,
        location_state: formData.locationState || null,
        location_country: 'AU',
        images: formData.images.length > 0 ? formData.images : null,
        broker_id: user.id,
        status: status === 'live' ? 'draft' : status, // Always create as draft first
        requires_nda: formData.requiresNDA,
      }

      const { data: listingResult, error } = await supabase
        .from('listings')
        .insert(listingData)
        .select('id')
        .single()

      if (error) {
        // Check if it's a permission error
        if (error.message?.includes('policy') || error.message?.includes('permission')) {
          throw new Error('You do not have permission to create listings. Only brokers and sellers can list businesses.')
        }
        throw error
      }

      if (status === 'live') {
        // Initiate payment flow for live listings
        const { data, error: paymentError } = await supabase.functions.invoke('create-listing-payment', {
          body: { 
            listing_id: listingResult.id
          },
        })

        if (paymentError) throw paymentError

        // Redirect to Stripe Checkout (avoid popup blockers)
        if (data?.url) {
          const newTab = window.open('', '_blank');
          if (newTab) {
            newTab.location.href = data.url;
          } else {
            // Fallback to same-tab navigation if the popup was blocked
            window.location.href = data.url;
          }
          toast({
            title: 'Payment Required',
            description: "Complete payment to publish your listing. You'll be redirected to the payment page.",
          })
          navigate('/broker-portal?section=listings');
        } else {
          toast({
            title: 'Payment Error',
            description: 'Could not start checkout. Please try again.',
            variant: 'destructive',
          })
        }
      } else {
        toast({
          title: 'Success',
          description: 'Business listing saved as draft successfully!',
        })
        navigate('/broker-portal?section=listings')
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const canPublish = formData.title && formData.sector && formData.locationState

  if (!user) {
    return null
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="shadow-medium">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-primary">List Your Business</CardTitle>
              <CardDescription>
                Create a professional listing to connect with qualified buyers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-6">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Basic Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="title">Business Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder="e.g., Profitable Restaurant in CBD"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="sector">Sector *</Label>
                    <Select value={formData.sector} onValueChange={(value) => handleInputChange('sector', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select business sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {BUSINESS_SECTORS.map(sector => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="teaser">Short Description</Label>
                    <Input
                      id="teaser"
                      value={formData.teaser}
                      onChange={(e) => handleInputChange('teaser', e.target.value)}
                      placeholder="Brief one-line description of your business"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Full Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      placeholder="Detailed description of the business, operations, and opportunity"
                      rows={4}
                    />
                  </div>
                </div>

                {/* Financial Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Financial Information</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="price">Asking Price</Label>
                    <Input
                      id="price"
                      type="number"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      placeholder="Asking price"
                    />
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="revenue">Annual Revenue</Label>
                      <Input
                        id="revenue"
                        type="number"
                        value={formData.revenue}
                        onChange={(e) => handleInputChange('revenue', e.target.value)}
                        placeholder="Last full year revenue"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ebitda">EBITDA</Label>
                      <Input
                        id="ebitda"
                        type="number"
                        value={formData.ebitda}
                        onChange={(e) => handleInputChange('ebitda', e.target.value)}
                        placeholder="Earnings before interest, tax, depreciation"
                      />
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Location</h3>
                  
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="locationSuburb">Suburb</Label>
                      <Input
                        id="locationSuburb"
                        value={formData.locationSuburb}
                        onChange={(e) => handleInputChange('locationSuburb', e.target.value)}
                        placeholder="Business suburb"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="locationState">State *</Label>
                      <Select value={formData.locationState} onValueChange={(value) => handleInputChange('locationState', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NSW">NSW</SelectItem>
                          <SelectItem value="VIC">VIC</SelectItem>
                          <SelectItem value="QLD">QLD</SelectItem>
                          <SelectItem value="WA">WA</SelectItem>
                          <SelectItem value="SA">SA</SelectItem>
                          <SelectItem value="TAS">TAS</SelectItem>
                          <SelectItem value="ACT">ACT</SelectItem>
                          <SelectItem value="NT">NT</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Images */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Business Images</h3>
                  <p className="text-sm text-muted-foreground">
                    Add up to 8 high-quality images of your business. First image will be used as the main photo.
                  </p>
                  
                  {/* Image Upload Area */}
                  <div className="space-y-4">
                    <div
                      className="border-2 border-dashed border-border rounded-lg p-6 text-center hover:border-primary/50 transition-colors cursor-pointer"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                      <p className="text-sm font-medium mb-1">Click to upload images</p>
                      <p className="text-xs text-muted-foreground">
                        PNG, JPG, WEBP up to 5MB each
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                        className="hidden"
                      />
                    </div>

                    {/* Image Previews */}
                    {formData.images.length > 0 && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {formData.images.map((image, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square overflow-hidden rounded-lg border bg-muted">
                              <img
                                src={image}
                                alt={`Business image ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute -top-2 -right-2 h-6 w-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => removeImage(index)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                            {index === 0 && (
                              <div className="absolute bottom-1 left-1 bg-primary text-primary-foreground text-xs px-1 py-0.5 rounded">
                                Main
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {uploadingImages && (
                      <div className="flex items-center justify-center py-4">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                          Processing images...
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Privacy & NDA Settings */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Privacy Settings</h3>
                  
                  <div className="flex items-start space-x-3 p-4 border rounded-lg">
                    <Checkbox
                      id="requiresNDA"
                      checked={formData.requiresNDA}
                      onCheckedChange={(checked) => handleInputChange('requiresNDA', checked ? 'true' : 'false')}
                    />
                    <div className="space-y-1 flex-1">
                      <Label htmlFor="requiresNDA" className="text-sm font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Require Non-Disclosure Agreement (NDA)
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Prospective buyers will need to sign an NDA before viewing detailed business information. 
                        This protects your confidential business data during the sales process.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={(e) => handleSubmit(e, 'draft')}
                    disabled={isLoading || !formData.title}
                    className="flex-1"
                  >
                    {isLoading ? 'Saving...' : 'Save as Draft'}
                  </Button>
                  <Button
                    type="button"
                    onClick={(e) => handleSubmit(e, 'live')}
                    disabled={isLoading || !canPublish}
                    className="flex-1"
                  >
                    {isLoading ? 'Publishing...' : 'Pay $98 & Publish'}
                  </Button>
                </div>
                
                {!canPublish && (
                  <p className="text-sm text-muted-foreground">
                    * Title, sector, and state are required to publish
                  </p>
                )}
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  )
}

export default ListBusiness